import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { getDocVersions, getDocVersion, diffDocVersions, createDocSnapshot, crossDiffDocs } from '../services/api';
import { toast } from 'sonner';
import {
  X, History, GitCompare, ChevronRight, Clock, User,
  Loader2, Plus, Minus, FileText, ArrowRight, RefreshCw, Link2
} from 'lucide-react';

const changeTypeLabels = {
  ai_suggestion: { label: 'AI Edit', color: 'bg-purple-100 text-purple-700' },
  manual_edit: { label: 'Manual', color: 'bg-blue-100 text-blue-700' },
  regeneration: { label: 'Regen', color: 'bg-amber-100 text-amber-700' },
  webhook_sync: { label: 'Webhook', color: 'bg-emerald-100 text-emerald-700' },
  initial_snapshot: { label: 'Snapshot', color: 'bg-slate-100 text-slate-600' },
};

const VersionHistoryPanel = ({ docId, docTitle, isOpen, onClose, onSelectVersion, regenerationLineage, onNavigateDoc }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diffMode, setDiffMode] = useState(false);
  const [selectedV1, setSelectedV1] = useState(null);
  const [selectedV2, setSelectedV2] = useState(null);
  const [diffResult, setDiffResult] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [crossDiffResult, setCrossDiffResult] = useState(null);
  const [loadingCrossDiff, setLoadingCrossDiff] = useState(false);
  const [activeTab, setActiveTab] = useState('versions'); // 'versions' | 'regeneration'

  useEffect(() => {
    if (isOpen && docId) fetchVersions();
    if (!isOpen) {
      setDiffMode(false);
      setDiffResult(null);
      setViewingVersion(null);
      setSelectedV1(null);
      setSelectedV2(null);
      setCrossDiffResult(null);
      setActiveTab('versions');
    }
  }, [isOpen, docId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const res = await getDocVersions(docId);
      setVersions(res.data);
    } catch {
      // Silently handle — doc might have no versions yet
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSnapshot = async () => {
    setSnapshotting(true);
    try {
      await createDocSnapshot(docId);
      toast.success('Version snapshot created');
      await fetchVersions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create snapshot');
    } finally {
      setSnapshotting(false);
    }
  };

  const handleViewVersion = async (v) => {
    setLoadingVersion(true);
    try {
      const res = await getDocVersion(docId, v.id);
      setViewingVersion(res.data);
    } catch {
      toast.error('Failed to load version');
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleToggleDiffSelect = (v) => {
    if (!selectedV1) {
      setSelectedV1(v);
    } else if (!selectedV2) {
      if (v.id === selectedV1.id) return;
      setSelectedV2(v);
    } else {
      // Reset and start new selection
      setSelectedV1(v);
      setSelectedV2(null);
      setDiffResult(null);
    }
  };

  const handleComputeDiff = async () => {
    if (!selectedV1 || !selectedV2) return;
    setLoadingDiff(true);
    try {
      const res = await diffDocVersions(docId, selectedV1.id, selectedV2.id);
      setDiffResult(res.data);
    } catch (err) {
      toast.error('Failed to compute diff');
    } finally {
      setLoadingDiff(false);
    }
  };

  const handleCrossDiff = async (oldDocId, newDocId) => {
    setLoadingCrossDiff(true);
    setCrossDiffResult(null);
    try {
      const res = await crossDiffDocs(oldDocId, newDocId);
      setCrossDiffResult(res.data);
    } catch (err) {
      toast.error('Failed to compute cross-document diff');
    } finally {
      setLoadingCrossDiff(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] lg:w-[560px] bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-violet-100 rounded-lg">
            <History className="h-4 w-4 text-violet-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Version History</h3>
            <p className="text-[11px] text-slate-500 truncate max-w-[200px]">{docTitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'versions' && (
            <Button
              variant={diffMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setDiffMode(!diffMode);
                setDiffResult(null);
                setSelectedV1(null);
                setSelectedV2(null);
                setViewingVersion(null);
              }}
              className={`h-7 text-xs ${diffMode ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
              data-testid="diff-mode-btn"
            >
              <GitCompare className="h-3 w-3 mr-1" />
              {diffMode ? 'Exit Diff' : 'Compare'}
            </Button>
          )}
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-200 text-slate-400" data-testid="close-version-panel">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      {regenerationLineage && regenerationLineage.length > 0 && (
        <div className="flex border-b border-slate-200 bg-white">
          <button
            onClick={() => { setActiveTab('versions'); setCrossDiffResult(null); }}
            className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === 'versions'
                ? 'text-violet-700 border-b-2 border-violet-600 bg-violet-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <History className="h-3 w-3 inline mr-1" />
            Versions
          </button>
          <button
            onClick={() => { setActiveTab('regeneration'); setDiffMode(false); setDiffResult(null); setViewingVersion(null); }}
            className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === 'regeneration'
                ? 'text-amber-700 border-b-2 border-amber-600 bg-amber-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <RefreshCw className="h-3 w-3 inline mr-1" />
            Regenerations ({regenerationLineage.length})
          </button>
        </div>
      )}

      <ScrollArea className="flex-1">
        {/* ===== VERSIONS TAB ===== */}
        {activeTab === 'versions' && (
          <>
        {/* Diff View */}
        {diffMode && !diffResult && (
          <div className="p-4">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-violet-800 mb-1">Compare Versions</p>
              <p className="text-xs text-violet-600">Select two versions below to see a side-by-side diff.</p>
              <div className="flex items-center gap-2 mt-3">
                <div className={`flex-1 h-8 rounded border-2 border-dashed flex items-center justify-center text-xs ${selectedV1 ? 'border-green-300 bg-green-50 text-green-700' : 'border-violet-300 text-violet-500'}`}>
                  {selectedV1 ? `v${selectedV1.version}` : 'Select older version'}
                </div>
                <ArrowRight className="h-4 w-4 text-violet-400" />
                <div className={`flex-1 h-8 rounded border-2 border-dashed flex items-center justify-center text-xs ${selectedV2 ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-violet-300 text-violet-500'}`}>
                  {selectedV2 ? `v${selectedV2.version}` : 'Select newer version'}
                </div>
              </div>
              {selectedV1 && selectedV2 && (
                <Button size="sm" onClick={handleComputeDiff} disabled={loadingDiff} className="w-full mt-3 bg-violet-600 hover:bg-violet-700 h-8 text-xs" data-testid="compute-diff-btn">
                  {loadingDiff ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <GitCompare className="h-3 w-3 mr-1" />}
                  Compare v{selectedV1.version} → v{selectedV2.version}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Diff Results */}
        {diffResult && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">v{diffResult.v1.version}</Badge>
                <ArrowRight className="h-3 w-3 text-slate-400" />
                <Badge variant="outline" className="text-xs">v{diffResult.v2.version}</Badge>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="flex items-center gap-1 text-green-600"><Plus className="h-3 w-3" />{diffResult.stats.additions}</span>
                <span className="flex items-center gap-1 text-red-500"><Minus className="h-3 w-3" />{diffResult.stats.deletions}</span>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setDiffResult(null); setSelectedV1(null); setSelectedV2(null); }} className="mb-3 h-7 text-xs text-violet-600">
              ← Pick different versions
            </Button>
            <div className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
              <div className="px-3 py-1.5 bg-slate-800 text-[10px] text-slate-400 flex items-center justify-between">
                <span>Diff: v{diffResult.v1.version} → v{diffResult.v2.version}</span>
                <span>{diffResult.stats.additions} additions, {diffResult.stats.deletions} deletions</span>
              </div>
              <div className="max-h-[60vh] overflow-y-auto p-0 font-mono text-[12px] leading-[1.6]">
                {diffResult.changes.map((change, i) => {
                  let bgClass = '';
                  let textClass = 'text-slate-400';
                  let prefix = ' ';
                  if (change.type === 'add') { bgClass = 'bg-green-900/30'; textClass = 'text-green-300'; prefix = '+'; }
                  else if (change.type === 'del') { bgClass = 'bg-red-900/30'; textClass = 'text-red-300'; prefix = '-'; }
                  else if (change.type === 'hunk') { bgClass = 'bg-violet-900/20'; textClass = 'text-violet-300'; prefix = ''; }

                  return (
                    <div key={i} className={`px-3 py-0.5 ${bgClass} ${textClass} whitespace-pre-wrap break-all`}>
                      {change.type === 'hunk' ? change.content : `${prefix} ${change.content}`}
                    </div>
                  );
                })}
                {diffResult.changes.length === 0 && (
                  <div className="p-4 text-center text-slate-500 text-xs">No differences found</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Version viewing */}
        {viewingVersion && !diffMode && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">v{viewingVersion.version}</Badge>
                <span className="text-xs text-slate-500">{viewingVersion.changed_by_name}</span>
                <span className="text-xs text-slate-400">{new Date(viewingVersion.created_at).toLocaleString()}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setViewingVersion(null)} className="h-7 text-xs">
                ← Back
              </Button>
            </div>
            {onSelectVersion && (
              <Button size="sm" onClick={() => { onSelectVersion(viewingVersion.content); onClose(); }} className="w-full mb-3 bg-violet-600 hover:bg-violet-700 h-8 text-xs" data-testid="restore-version-btn">
                Restore this version
              </Button>
            )}
            <div className="bg-white rounded-lg border border-slate-200 p-4 max-h-[60vh] overflow-y-auto">
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">{viewingVersion.content}</pre>
            </div>
          </div>
        )}

        {/* Version List */}
        {!viewingVersion && (!diffMode || !diffResult) && (
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-12">
                <History className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700 mb-1">No version history yet</p>
                <p className="text-xs text-slate-500 mb-4">Create a snapshot to start tracking changes</p>
                <Button size="sm" onClick={handleCreateSnapshot} disabled={snapshotting} className="bg-violet-600 hover:bg-violet-700 h-8 text-xs" data-testid="create-snapshot-btn">
                  {snapshotting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
                  Create Snapshot
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {versions.length} Version{versions.length !== 1 ? 's' : ''}
                  </p>
                  <Button variant="ghost" size="sm" onClick={handleCreateSnapshot} disabled={snapshotting} className="h-6 text-[10px] text-violet-600" data-testid="create-snapshot-btn">
                    {snapshotting ? <Loader2 className="h-3 w-3 animate-spin" /> : '+ Snapshot'}
                  </Button>
                </div>
                {versions.map((v, i) => {
                  const cType = changeTypeLabels[v.change_type] || changeTypeLabels.manual_edit;
                  const isSelectedV1 = selectedV1?.id === v.id;
                  const isSelectedV2 = selectedV2?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => diffMode ? handleToggleDiffSelect(v) : handleViewVersion(v)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all group border ${
                        isSelectedV1 ? 'border-green-300 bg-green-50' :
                        isSelectedV2 ? 'border-blue-300 bg-blue-50' :
                        'border-transparent hover:bg-slate-50 hover:border-slate-200'
                      }`}
                      data-testid={`version-item-${v.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] font-bold h-5 px-1.5">v{v.version}</Badge>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cType.color}`}>{cType.label}</span>
                          {i === 0 && <Badge className="bg-violet-100 text-violet-700 text-[9px] h-4 px-1">Latest</Badge>}
                        </div>
                        {!diffMode && <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-500" />}
                        {diffMode && (isSelectedV1 || isSelectedV2) && (
                          <span className={`text-[10px] font-bold ${isSelectedV1 ? 'text-green-600' : 'text-blue-600'}`}>
                            {isSelectedV1 ? 'FROM' : 'TO'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 ml-1">
                        <span className="text-[11px] text-slate-500 flex items-center gap-1">
                          <User className="h-3 w-3" />{v.changed_by_name}
                        </span>
                        <span className="text-[11px] text-slate-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />{new Date(v.created_at).toLocaleString()}
                        </span>
                      </div>
                      {v.change_summary && (
                        <p className="text-[11px] text-slate-500 mt-1 ml-1 truncate">{v.change_summary}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
          </>
        )}

        {/* ===== REGENERATION HISTORY TAB ===== */}
        {activeTab === 'regeneration' && (
          <div className="p-4">
            {/* Cross-diff result view */}
            {crossDiffResult ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{crossDiffResult.doc1.title} v{crossDiffResult.doc1.version}</Badge>
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                    <Badge variant="outline" className="text-xs">{crossDiffResult.doc2.title} v{crossDiffResult.doc2.version}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 text-green-600"><Plus className="h-3 w-3" />{crossDiffResult.additions}</span>
                    <span className="flex items-center gap-1 text-red-500"><Minus className="h-3 w-3" />{crossDiffResult.deletions}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setCrossDiffResult(null)} className="mb-3 h-7 text-xs text-amber-600">
                  ← Back to regeneration list
                </Button>
                <div className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
                  <div className="px-3 py-1.5 bg-slate-800 text-[10px] text-slate-400 flex items-center justify-between">
                    <span>Cross-document diff</span>
                    <span>{crossDiffResult.additions} additions, {crossDiffResult.deletions} deletions</span>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto p-0 font-mono text-[12px] leading-[1.6]">
                    {crossDiffResult.changes.map((change, i) => {
                      let bgClass = '';
                      let textClass = 'text-slate-400';
                      let prefix = ' ';
                      if (change.type === 'add') { bgClass = 'bg-green-900/30'; textClass = 'text-green-300'; prefix = '+'; }
                      else if (change.type === 'del') { bgClass = 'bg-red-900/30'; textClass = 'text-red-300'; prefix = '-'; }
                      else if (change.type === 'hunk') { bgClass = 'bg-violet-900/20'; textClass = 'text-violet-300'; prefix = ''; }
                      return (
                        <div key={i} className={`px-3 py-0.5 ${bgClass} ${textClass} whitespace-pre-wrap break-all`}>
                          {change.type === 'hunk' ? change.content : `${prefix} ${change.content}`}
                        </div>
                      );
                    })}
                    {crossDiffResult.changes.length === 0 && (
                      <div className="p-4 text-center text-slate-500 text-xs">No differences found</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Regeneration lineage list */
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                  <p className="text-xs text-amber-700">
                    <RefreshCw className="h-3 w-3 inline mr-1" />
                    Each regeneration creates a new document from stored source files. Compare any two generations below.
                  </p>
                </div>

                {(!regenerationLineage || regenerationLineage.length === 0) ? (
                  <div className="text-center py-12">
                    <RefreshCw className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-700 mb-1">No regeneration history</p>
                    <p className="text-xs text-slate-500">This document has not been regenerated yet.</p>
                  </div>
                ) : (
                  regenerationLineage.map((link) => (
                    <div key={link.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-all">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-xs font-medium text-slate-700">Regeneration</span>
                        <span className="text-[10px] text-slate-400">{new Date(link.created_at).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2 ml-5">
                        <Badge variant="outline" className="text-[10px]">v{link.old_version}</Badge>
                        <ArrowRight className="h-3 w-3 text-slate-400" />
                        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">v{link.new_version}</Badge>
                        <span className="text-[10px] text-slate-400 ml-1">by {link.changed_by_name}</span>
                      </div>
                      <div className="flex items-center gap-2 ml-5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCrossDiff(link.old_doc_id, link.new_doc_id)}
                          disabled={loadingCrossDiff}
                          className="h-6 text-[10px] border-amber-200 text-amber-700 hover:bg-amber-50"
                          data-testid={`cross-diff-${link.id}`}
                        >
                          {loadingCrossDiff ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <GitCompare className="h-3 w-3 mr-1" />}
                          Compare generations
                        </Button>
                        {onNavigateDoc && (
                          <>
                            {link.old_doc_id !== docId && (
                              <Button size="sm" variant="ghost" onClick={() => { onNavigateDoc(link.old_doc_id); onClose(); }} className="h-6 text-[10px] text-slate-500">
                                Go to v{link.old_version}
                              </Button>
                            )}
                            {link.new_doc_id !== docId && (
                              <Button size="sm" variant="ghost" onClick={() => { onNavigateDoc(link.new_doc_id); onClose(); }} className="h-6 text-[10px] text-slate-500">
                                Go to v{link.new_version}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default VersionHistoryPanel;
